import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { SupportService } from 'src/app/services/support.service';
import { ApiService } from 'src/app/services/api.service';

@Component({
  selector: 'app-ticket-detail',
  templateUrl: './ticket-detail.component.html',
  styleUrls: ['./ticket-detail.component.scss']
})
export class TicketDetailComponent implements OnInit {
  ticket: any = null;
  messages: any[] = [];
  role: any = localStorage.getItem('role');
  id = '';

  selectedStatus = '';
  assignAdminId = '';

  replyForm!: FormGroup;
  isInternal = false;
  replyAttachments: File[] = [];
  replyAttachmentError = '';
  loadingTicket = false;
  loadingMessages = false;
  submittingReply = false;
  updatingStatus = false;
  assigning = false;

  constructor(
    private supportService: SupportService,
    private api: ApiService,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    private fb: FormBuilder
  ) {}

  ngOnInit() {
    this.role = localStorage.getItem('role');
    this.id = this.route.snapshot.paramMap.get('id') || '';
    if (!this.id) {
      this.router.navigate(['/support']);
      return;
    }

    this.replyForm = this.fb.group({
      content: ['', [Validators.required]]
    });

    this.loadTicket();
    this.loadMessages();
  }

  loadTicket() {
    this.loadingTicket = true;
    this.supportService.getTicket(this.id).subscribe(
      (res: any) => {
        this.loadingTicket = false;
        if (res && res.response && res.response.data) {
          this.ticket = res.response.data.ticket || res.response.data;
          this.selectedStatus = this.ticket.status;
          this.assignAdminId = this.ticket.assignedTo?._id || this.ticket.assignedTo || '';
        }
      },
      (err: any) => {
        this.loadingTicket = false;
        this.toastr.error(err?.error?.response?.message || 'Failed to load ticket');
      }
    );
  }

  loadMessages() {
    this.loadingMessages = true;
    this.supportService.getMessages(this.id).subscribe(
      (res: any) => {
        this.loadingMessages = false;
        if (res && res.response && res.response.data) {
          const msgs = res.response.data.messages || res.response.data || [];
          if (this.role !== 'admin') {
            this.messages = msgs.filter((m: any) => !m.isInternal);
          } else {
            this.messages = msgs;
          }
        }
      },
      (err: any) => {
        this.loadingMessages = false;
        console.error('Failed to load messages', err);
      }
    );
  }

  onStatusChange() {
    if (!this.selectedStatus) { return; }
    this.updatingStatus = true;
    this.supportService.updateTicketStatus(this.id, this.selectedStatus).subscribe(
      (res: any) => {
        this.updatingStatus = false;
        this.toastr.success('Status updated successfully');
        this.loadTicket();
      },
      (err: any) => {
        this.updatingStatus = false;
        this.toastr.error(err?.error?.response?.message || 'Failed to update status');
      }
    );
  }

  onAssign() {
    if (!this.assignAdminId) { return; }
    this.assigning = true;
    this.supportService.assignTicket(this.id, this.assignAdminId).subscribe(
      (res: any) => {
        this.assigning = false;
        this.toastr.success('Ticket assigned successfully');
        this.loadTicket();
      },
      (err: any) => {
        this.assigning = false;
        this.toastr.error(err?.error?.response?.message || 'Failed to assign ticket');
      }
    );
  }

  onReplyFileSelect(event: Event) {
    this.replyAttachmentError = '';
    const input = event.target as HTMLInputElement;
    if (!input.files) { return; }

    const newFiles = Array.from(input.files);
    const combined = [...this.replyAttachments, ...newFiles];

    if (combined.length > 5) {
      this.replyAttachmentError = 'Maximum 5 attachments allowed.';
      return;
    }
    for (const file of combined) {
      if (file.size > 10 * 1024 * 1024) {
        this.replyAttachmentError = `File "${file.name}" exceeds the 10 MB limit.`;
        return;
      }
    }
    this.replyAttachments = combined;
    input.value = '';
  }

  removeReplyAttachment(index: number) {
    this.replyAttachments.splice(index, 1);
    this.replyAttachmentError = '';
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) { return bytes + ' B'; }
    if (bytes < 1024 * 1024) { return (bytes / 1024).toFixed(1) + ' KB'; }
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  onReplySubmit() {
    if (this.replyForm.invalid) {
      this.replyForm.markAllAsTouched();
      return;
    }

    this.submittingReply = true;
    const formData = new FormData();
    formData.append('content', this.replyForm.value.content);
    formData.append('isInternal', String(this.isInternal));
    this.replyAttachments.forEach(file => {
      formData.append('attachments', file, file.name);
    });

    this.supportService.addMessage(this.id, formData).subscribe(
      (res: any) => {
        this.submittingReply = false;
        this.toastr.success('Reply sent successfully');
        this.replyForm.reset();
        this.replyAttachments = [];
        this.isInternal = false;
        this.loadMessages();
      },
      (err: any) => {
        this.submittingReply = false;
        this.toastr.error(err?.error?.response?.message || 'Failed to send reply');
      }
    );
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'open': return 'warning';
      case 'in_progress': return 'primary';
      case 'resolved': return 'success';
      case 'closed': return 'basic';
      default: return 'info';
    }
  }

  getPriorityColor(priority: string): string {
    switch (priority) {
      case 'urgent': return 'danger';
      case 'high': return 'warning';
      case 'normal': return 'info';
      case 'low': return 'basic';
      default: return 'basic';
    }
  }

  getAttachmentUrl(filename: string): string {
    return this.api.imageUrl + 'uploads/support/' + filename;
  }

  goBack() {
    this.router.navigate(['/support']);
  }
}
